import React, {useState} from "react";
import {Button} from "primereact/button";
import {InputSwitch} from "primereact/inputswitch";
import {Tag} from "primereact/tag";
import "./CanarySiteBands.css";

// Presentation for the canary site list. Canary.js computes the numbers and hands over one
// prepared item per rule (see buildSites there); everything below only decides how loud each
// site gets to be. The screen outgrew its original "A/B by site" frame — most sites now run a
// single in-house arm — so sites are grouped by health instead of listed in a flat grid: a
// broken site gets a full card, a healthy one gets a line.

// Health thresholds. Shared with the big-number colouring so a site's colour and its band can
// never disagree (a 96% site must not read green while sitting under "needs attention").
export const CRITICAL_BELOW = 90;
export const HEALTHY_AT = 98;

export const rateColor = (r) => r == null ? 'var(--text-color-secondary)'
    : (r >= HEALTHY_AT ? '#22c55e' : (r >= CRITICAL_BELOW ? '#f59e0b' : '#ef4444'));

// Proxy attempts per business request. The success rate only moves once the retry budget is
// exhausted, so a site that is starting to fail spends more attempts per request while still
// reading green -- amazon.com.tr sat at 91% on 746 attempts per request the day its offer path
// broke. Deliberately uncoloured: every host has its own healthy baseline (trendyol runs at
// 1.02, hepsiburada at 1.36), so a shared threshold would cry wolf on half the list. Read it
// against the host's own history, not against the other rows.
//
// BOTH arguments must come from proxy_metrics (proxy_attempts / proxy_requests). The canary
// request count is NOT the denominator: it counts the host an outcome was recorded under,
// while an attempt is counted against the host actually fetched, and for aux-host spiders
// those differ -- dividing across the two read x6017 on watsons traffic that cost x1.25.
export const retryRatio = (requests, attempts) =>
    (attempts == null || !requests) ? null : Math.round((attempts / requests) * 100) / 100;

// Exactly one state per rule, first match wins. `ab` sits above the health states because a
// site under a real two-arm test is read as a comparison, not as a health number.
export const siteState = (rule, cmp) => {
    if (!(rule.canary && rule.canary.enabled !== false)) return 'paused';
    const canary = cmp && cmp.canary;
    if (!canary || !canary.attempts || canary.successRate == null) return 'no_data';
    if (cmp.baseline) return 'ab';
    if (canary.successRate < CRITICAL_BELOW) return 'critical';
    if (canary.successRate < HEALTHY_AT) return 'warning';
    return 'healthy';
};

const numberFmt = (n) => (n || 0).toLocaleString();

const CanarySiteBands = ({t, sites, fltHost, setFltHost, onToggle, onEdit, onDelete,
                          onSiteRule, onProxies, armCard}) => {

    const [openHealthy, setOpenHealthy] = useState(false);
    const [openSilent, setOpenSilent] = useState(false);

    const of = (...states) => sites.filter(s => states.includes(s.state));
    const attention = of('critical', 'warning');
    const abTests = of('ab');
    const healthy = of('healthy');
    const silent = of('paused', 'no_data');

    // Day-over-day movement of the canary arm. This replaces the old A/B delta on the card,
    // which stayed empty on every site that has no baseline arm — i.e. almost all of them.
    const deltaChip = (dod) => {
        if (dod == null || dod === 0) return null;
        const color = dod > 0 ? '#22c55e' : (dod < 0 ? '#ef4444' : 'var(--text-color-secondary)');
        const icon = dod > 0 ? 'pi-arrow-up' : (dod < 0 ? 'pi-arrow-down' : 'pi-minus');
        return <span title={t('canary.dod_delta')} style={{fontSize: '0.82em', fontWeight: 600, color}}>
            <i className={'pi ' + icon} style={{fontSize: '0.72em', marginRight: '3px'}}/>
            {Math.abs(dod)}
        </span>;
    };

    const failuresInline = (failures) => failures.length ? (
        <React.Fragment>
            {failures.map((f, i) => (
                <span key={f.reason}>
                    {i > 0 ? ' · ' : ''}
                    <span style={{color: '#ef4444'}}>{f.reason} {numberFmt(f.count)}</span>
                </span>
            ))}
        </React.Fragment>
    ) : null;

    // A gone page is not a failure -- the fetch worked and the site said the product is no
    // longer listed -- so it reads in the ordinary secondary colour rather than the failure red.
    const infoInline = (info) => info.length ? (
        <React.Fragment>
            {info.map((f, i) => (
                <span key={f.reason}>
                    {i > 0 ? ' · ' : ''}
                    <span>{f.reason} {numberFmt(f.count)}</span>
                </span>
            ))}
        </React.Fragment>
    ) : null;

    // Raw, and in the ordinary secondary colour: this is a number to compare against the same
    // host yesterday, not a verdict. The tooltip carries the two counts it came from because
    // the ratio alone cannot tell a quiet site from a busy one.
    const retryInline = (s) => {
        const ratio = retryRatio(s.proxyRequests, s.proxyAttempts);
        if (ratio == null) return null;
        return <React.Fragment>
            {' · '}
            <span title={t('canary.retry_ratio_hint',
                           {attempts: numberFmt(s.proxyAttempts), requests: numberFmt(s.proxyRequests)})}>
                ×{ratio.toFixed(2)}
            </span>
        </React.Fragment>;
    };

    // Pool size is a fact about the pool, not about the site — it used to repeat the same
    // "900/1010" across half the cards. It only earns space when something is actually off
    // (a disabled proxy) or when a site rule narrows the pool for this host.
    const proxyTag = (s) => s.proxies.show ? (
        <span title={t('canary.proxies')} style={{cursor: 'pointer'}}
              onClick={(e) => { e.stopPropagation(); onProxies(s.rule); }}>
            <Tag value={s.proxies.active + '/' + s.proxies.total} severity={s.proxies.severity}/>
        </span>
    ) : (
        <span style={{fontSize: '0.76em', color: 'var(--text-color-secondary)', cursor: 'pointer'}}
              onClick={(e) => { e.stopPropagation(); onProxies(s.rule); }}>
            {t('canary.proxy_count', {count: s.proxies.total})}
        </span>
    );

    const rowActions = (s, hoverOnly) => (
        <span onClick={(e) => e.stopPropagation()} className={hoverOnly ? 'canary-row-actions' : ''}
              style={{display: 'flex', gap: '2px'}}>
            <Button icon="pi pi-sliders-h" className="p-button-info p-button-text p-button-sm"
                    tooltip={t('canary.edit_site_rule')} tooltipOptions={{position: 'bottom'}}
                    onClick={() => onSiteRule(s.host)}/>
            <Button icon="pi pi-pencil" className="p-button-warning p-button-text p-button-sm"
                    tooltip={t('actions.edit')} tooltipOptions={{position: 'bottom'}}
                    onClick={() => onEdit(s.rule)}/>
            <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-sm"
                    tooltip={t('actions.remove')} tooltipOptions={{position: 'bottom'}}
                    onClick={() => onDelete(s.rule)}/>
        </span>
    );

    const cardShell = (s, accent, children) => {
        const selected = fltHost === s.host;
        return <div key={s.host} onClick={() => setFltHost(selected ? null : s.host)}
                    style={{cursor: 'pointer', flex: '1 1 300px', minWidth: 0, maxWidth: '420px',
                            border: '1px solid ' + (selected ? 'var(--primary-color, #3b82f6)' : accent),
                            background: 'var(--surface-card)', borderRadius: '12px', padding: '13px 15px',
                            boxShadow: selected ? '0 0 0 1px var(--primary-color, #3b82f6), 0 4px 14px rgba(59,130,246,0.20)'
                                : '0 1px 4px rgba(0,0,0,0.08)',
                            transition: 'box-shadow .12s, border-color .12s'}}>
            {children}
        </div>;
    };

    const cardHeader = (s) => (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px'}}>
            <span style={{fontWeight: 600, minWidth: 0, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{s.host}</span>
            <span onClick={(e) => e.stopPropagation()}>
                <InputSwitch checked={s.enabled} onChange={() => onToggle(s.rule)}/>
            </span>
        </div>
    );

    // Meta line: provider chain, plus the canary sampling rate ONLY when it is not the full
    // 100%. Shown as a full-width badge next to the host it read like a second success rate.
    const metaLine = (s) => (
        <div style={{fontSize: '0.78em', color: 'var(--text-color-secondary)'}}>
            {s.meta.join(' · ')}
            {s.trafficRate != null ? ' · ' + t('canary.traffic_rate', {rate: s.trafficRate}) : ''}
        </div>
    );

    // Border follows the same threshold as the number, so a watch-level site never wears a
    // critical frame.
    const attentionCard = (s) => cardShell(s, rateColor(s.rate), <React.Fragment>
        {cardHeader(s)}
        <div style={{display: 'flex', alignItems: 'baseline', gap: '10px', margin: '10px 0 4px'}}>
            <span style={{fontSize: '32px', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.5px',
                          color: rateColor(s.rate)}}>{s.rate}%</span>
            {deltaChip(s.dod)}
        </div>
        <div style={{fontSize: '0.8em', color: 'var(--text-color-secondary)', marginBottom: '8px'}}>
            {numberFmt(s.attempts)} {t('canary.attempts').toLowerCase()}
            {retryInline(s)}
            {s.failures.length ? <React.Fragment> · {failuresInline(s.failures)}</React.Fragment> : null}
            {s.info && s.info.length ? <React.Fragment> · {infoInline(s.info)}</React.Fragment> : null}
        </div>
        {metaLine(s)}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                     borderTop: '1px solid var(--surface-border)', marginTop: '9px', paddingTop: '6px'}}>
            {proxyTag(s)}
            {rowActions(s)}
        </div>
    </React.Fragment>);

    const abCard = (s) => cardShell(s, 'var(--surface-border)', <React.Fragment>
        {cardHeader(s)}
        <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '10px 0 8px'}}>
            {armCard(s.cmp.canary, true)}
            {armCard(s.cmp.baseline, false)}
        </div>
        {s.cmp.delta != null &&
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                <Tag value={'Δ ' + (s.cmp.delta >= 0 ? '+' : '') + s.cmp.delta + ' ' + t('canary.points')}
                     severity={s.cmp.delta > 0 ? 'success' : (s.cmp.delta < 0 ? 'danger' : 'info')}/>
                <span style={{color: 'var(--text-color-secondary)', fontSize: '0.82em'}}>
                    {s.cmp.delta > 0 ? t('canary.ahead') : (s.cmp.delta < 0 ? t('canary.behind') : t('canary.even'))}
                </span>
            </div>}
        {metaLine(s)}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                     borderTop: '1px solid var(--surface-border)', marginTop: '9px', paddingTop: '6px'}}>
            {proxyTag(s)}
            {rowActions(s)}
        </div>
    </React.Fragment>);

    // Collapsed bands render one line per site: enough to confirm the site is fine and to
    // reach its controls, without spending a card on it.
    const compactRow = (s, variant, cells) => {
        const selected = fltHost === s.host;
        return <div key={s.host} onClick={() => setFltHost(selected ? null : s.host)}
                    className={'canary-row canary-row-' + variant + (selected ? ' canary-row-selected' : '')}>
            <span className="canary-row-host">{s.host}</span>
            {cells}
            <span onClick={(e) => e.stopPropagation()}>
                <InputSwitch checked={s.enabled} onChange={() => onToggle(s.rule)}/>
            </span>
            {rowActions(s, true)}
        </div>;
    };

    const healthyRow = (s) => compactRow(s, 'healthy', <React.Fragment>
        <span className="canary-row-rate" style={{color: rateColor(s.rate)}}>{s.rate}%</span>
        <span className="canary-row-delta">{deltaChip(s.dod)}</span>
        <span className="canary-row-attempts">
            {numberFmt(s.attempts)} {t('canary.attempts').toLowerCase()}
            {retryInline(s)}
        </span>
    </React.Fragment>);

    const silentRow = (s) => compactRow(s, 'silent',
        <span className="canary-row-reason">
            {s.state === 'paused' ? t('canary.state_paused') : t('canary.state_no_data')}
        </span>);

    const bandTitle = (icon, color, label, count) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 9px'}}>
            <i className={'pi ' + icon} style={{color, fontSize: '0.95em'}}/>
            <span style={{fontWeight: 600}}>{label}</span>
            <span style={{color: 'var(--text-color-secondary)', fontSize: '0.88em'}}>{count}</span>
        </div>
    );

    const collapsedBand = (open, setOpen, label, items, summary, rowFn) => items.length ? (
        <div style={{border: '1px solid var(--surface-border)', borderRadius: '12px',
                     background: 'var(--surface-card)'}}>
            <div onClick={() => setOpen(!open)}
                 style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                         padding: '11px 15px', flexWrap: 'wrap'}}>
                <i className={'pi ' + (open ? 'pi-chevron-down' : 'pi-chevron-right')}
                   style={{color: 'var(--text-color-secondary)', fontSize: '0.85em'}}/>
                <span style={{fontWeight: 600}}>{label}</span>
                <span style={{color: 'var(--text-color-secondary)', fontSize: '0.88em'}}>{items.length}</span>
                <span style={{flex: 1}}/>
                {!open && <span style={{fontSize: '0.8em', color: 'var(--text-color-secondary)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {summary}</span>}
            </div>
            {open && <div style={{padding: '0 15px 8px'}}>{items.map(rowFn)}</div>}
        </div>
    ) : null;

    const counter = (label, count, bg, color, onClick) => (
        <div onClick={onClick} style={{flex: '0 1 150px', background: bg, borderRadius: '10px',
                                       padding: '9px 13px', cursor: onClick ? 'pointer' : 'default'}}>
            <div style={{fontSize: '0.78em', color}}>{label}</div>
            <div style={{fontSize: '22px', fontWeight: 700, color, lineHeight: 1.25}}>{count}</div>
        </div>
    );

    const healthySummary = healthy.slice(0, 3).map(s => s.host.replace(/^www\./, '') + ' ' + s.rate).join(' · ')
        + (healthy.length > 3 ? ' · +' + (healthy.length - 3) : '');
    const silentSummary = silent.slice(0, 3).map(s => s.host).join(', ')
        + (silent.length > 3 ? ' · +' + (silent.length - 3) : '');

    return <React.Fragment>

        <div style={{display: 'flex', gap: '9px', flexWrap: 'wrap', marginBottom: '18px'}}>
            {counter(t('canary.state_critical'), of('critical').length, 'rgba(239,68,68,0.10)', '#b91c1c')}
            {counter(t('canary.state_warning'), of('warning').length, 'rgba(245,158,11,0.12)', '#b45309')}
            {counter(t('canary.state_ab'), abTests.length, 'rgba(59,130,246,0.10)', '#1d4ed8')}
            {counter(t('canary.state_healthy'), healthy.length, 'rgba(34,197,94,0.12)', '#15803d',
                     () => setOpenHealthy(!openHealthy))}
            {counter(t('canary.state_silent'), silent.length, 'var(--surface-ground, #f1f5f9)',
                     'var(--text-color-secondary)', () => setOpenSilent(!openSilent))}
        </div>

        {attention.length > 0 &&
            <div style={{marginBottom: '20px'}}>
                {bandTitle('pi-exclamation-triangle', '#ef4444', t('canary.band_attention'), attention.length)}
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>{attention.map(attentionCard)}</div>
            </div>}

        {abTests.length > 0 &&
            <div style={{marginBottom: '20px'}}>
                {bandTitle('pi-chart-bar', 'var(--primary-color, #3b82f6)', t('canary.band_ab'), abTests.length)}
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>{abTests.map(abCard)}</div>
            </div>}

        {attention.length === 0 && abTests.length === 0 && sites.length > 0 &&
            <div style={{border: '1px solid var(--surface-border)', borderRadius: '12px',
                         background: 'var(--surface-card)', padding: '18px', textAlign: 'center',
                         color: 'var(--text-color-secondary)'}}>
                <i className="pi pi-check-circle" style={{color: '#22c55e', marginRight: '7px'}}/>
                {t('canary.all_healthy')}
            </div>}

        {(healthy.length > 0 || silent.length > 0) && <div className="canary-collapsed-bands">
            {collapsedBand(openHealthy, setOpenHealthy, t('canary.state_healthy'), healthy, healthySummary, healthyRow)}
            {collapsedBand(openSilent, setOpenSilent, t('canary.state_silent'), silent, silentSummary, silentRow)}
        </div>}

    </React.Fragment>;
};

export default CanarySiteBands;
